import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import type { EnvironmentsGetEnvironmentResponse, SettingsWorkspaceSettings } from '@customerio/hydra-ui-api';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { hydraUiApi } from '@/services/api';
import { fetchAccountById } from '@/services/auth/account-api';
import {
  environmentKeys,
  parseAccountWorkspace,
  parseEnvironment,
  sampleAccountWorkspace,
  type MergedWorkspace,
  type SampleWorkspaceRef,
  type WorkspaceEnvironment,
} from './entity';

const STALE_TIME = 60_000;

function normalizeList<T>(values: readonly T[] | null | undefined): T[] {
  return values == null ? [] : [...values];
}

function useValidWorkspaceId(): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

/** @public */
export async function getEnvironment(workspaceId: number, signal?: AbortSignal): Promise<WorkspaceEnvironment> {
  const raw = await hydraUiApi.environments.get({
    params: { path: { environment_id: String(workspaceId) } },
    signal,
  });
  return parseEnvironment(normalizeEnvironmentResponse(raw));
}

function normalizeEnvironmentResponse(raw: EnvironmentsGetEnvironmentResponse): EnvironmentsGetEnvironmentResponse {
  const environment = raw.environment;

  return {
    ...raw,
    environment:
      environment == null
        ? undefined
        : {
            ...environment,
            device_attributes: normalizeList(environment.device_attributes),
            enabled_actions: normalizeList(environment.enabled_actions),
            identity_ids: normalizeList(environment.identity_ids),
            object_type_ids: normalizeList(environment.object_type_ids),
            push_providers: normalizeList(environment.push_providers),
            sent_attributes: normalizeList(environment.sent_attributes),
            sent_events: normalizeList(environment.sent_events),
            user_ids: normalizeList(environment.user_ids),
          },
    identities: normalizeList(raw.identities),
    object_types: normalizeList(raw.object_types),
  };
}

/** @public */
export const getEnvironmentQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: environmentKeys.current(workspaceId),
    queryFn: () => getEnvironment(workspaceId),
    staleTime: STALE_TIME,
  });

/**
 * @public The workspace's enabled, non-CDP tracking site ids, in the order
 * services auto-selects from. Unlike the API credentials list, disabled
 * credentials are left out.
 */
export async function getSiteIds(workspaceId: number): Promise<string[]> {
  const data = await hydraUiApi.environments.getSiteIds({
    params: { path: { environment_id: String(workspaceId) } },
  });
  return data.site_ids;
}

/** `staleTime: 0`: credentials are edited on the API credentials page, so a revisit refetches. */
export const getSiteIdsQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: environmentKeys.siteIds(workspaceId),
    queryFn: () => getSiteIds(workspaceId),
    staleTime: 0,
  });

export const workspaceSettingsKeys = {
  detail: (workspaceId: number) => ['workspace-settings', workspaceId] as const,
};

/** @public */
export async function getWorkspaceSettings(workspaceId: number, signal?: AbortSignal) {
  return hydraUiApi.workspaceSettings.getSettingsWorkspace({
    params: { path: { environment_id: String(workspaceId) } },
    signal,
  });
}

export type UpdateWorkspaceSettingsInput<
  TSettings extends Partial<SettingsWorkspaceSettings> = Partial<SettingsWorkspaceSettings>,
> = {
  workspaceId: number;
  settings: TSettings;
};

/** @public */
export async function updateWorkspaceSettings<TSettings extends Partial<SettingsWorkspaceSettings>>(
  input: UpdateWorkspaceSettingsInput<TSettings>,
) {
  return hydraUiApi.workspaceSettings.updateSettingsWorkspace({
    params: { path: { environment_id: String(input.workspaceId) } },
    body: { settings: input.settings },
  });
}

/**
 * Non-suspense environment read for the current workspace. Reads the
 * workspace id from the URL and routes to `skipToken` until it resolves —
 * the same "optional" pattern as `useDomainsOptional`, so callers that may
 * render before the workspace is in scope don't register a `(0)` slot.
 *
 * Pass `workspaceIdOverride` (e.g. `useCurrentWorkspace()?.id`) for callers that
 * render on non-numeric routes like `/workspaces/last/...`, where the URL has no
 * numeric workspace segment but the current workspace is still resolved.
 *
 * @public
 */
type UseEnvironmentOptionalOptions = {
  enabled?: boolean;
};

export function useEnvironmentOptional(workspaceIdOverride?: number, options: UseEnvironmentOptionalOptions = {}) {
  const urlWorkspaceId = useValidWorkspaceId();
  const validWorkspaceId =
    workspaceIdOverride != null && workspaceIdOverride > 0 ? workspaceIdOverride : urlWorkspaceId;
  return useQuery({
    queryKey: validWorkspaceId != null ? environmentKeys.current(validWorkspaceId) : ['environment', 'no-workspace'],
    queryFn: validWorkspaceId != null ? () => getEnvironment(validWorkspaceId) : skipToken,
    staleTime: STALE_TIME,
    enabled: options.enabled,
  });
}

/**
 * The account's workspaces, with the sample workspace last when the user can
 * see one. An account whose user has no workspaces (a billing-only user) reads
 * as an empty list.
 */
export function useEnvironmentsOptional<TSelected = MergedWorkspace[]>({
  accountId,
  sampleWorkspace,
  enabled = true,
  select,
  meta,
}: {
  accountId: number | null | undefined;
  sampleWorkspace: SampleWorkspaceRef | null;
  enabled?: boolean;
  select?: (workspaces: MergedWorkspace[]) => TSelected;
  meta?: { errorMessage: string };
}) {
  return useQuery({
    queryKey:
      accountId != null
        ? environmentKeys.accountList(accountId, sampleWorkspace)
        : environmentKeys.inactive('account-list'),
    queryFn:
      accountId != null
        ? async () => {
            const { environments } = await fetchAccountById(accountId);
            const workspaces = (environments ?? []).map(parseAccountWorkspace);
            if (sampleWorkspace != null) workspaces.push(sampleAccountWorkspace(sampleWorkspace));
            return workspaces;
          }
        : skipToken,
    select,
    meta,
    staleTime: 60 * 1000,
    enabled,
  });
}
